/**
 * Recurring follow-up / cadence engine (Ask #1).
 *
 * Every live account should be touched on a rhythm — the bigger the
 * relationship, the tighter the rhythm. This module is the PURE core of that
 * engine: it derives a per-account cadence rule (check-in every N days, QBR
 * every M days) from real signals on the record — ARR tier and lifecycle
 * status — and computes which accounts have gone quiet past their cadence.
 *
 * Design constraints, mirroring `health.ts` / `reminders.ts`:
 *   - Pure selectors over snapshots — nothing is stored, so it can't drift.
 *   - "Last meaningful touch" comes only from real signals: the account's
 *     email trail and its linked people's touch timeline (`lastTouchAt`).
 *     Nothing is invented; a never-touched account is reported as exactly
 *     that, anchored to its own createdAt for the grace window.
 *   - The engine only *describes* what's due. Proposing the check-in is a
 *     governed action (see `agent/cadenceAgent.ts`), queued into the
 *     approvals inbox — never auto-written.
 *
 * Accounts link to people the same way `weeklyUpdate.ts` resolves
 * recipients: a Person whose `company` matches the account name
 * (case-insensitive).
 */

import type { Account, Person } from '../db/types';
import type { CreateTaskAction } from '../agent/types';
import { lastTouchAt } from './score';

const DAY_MS = 86_400_000;

// ---------------------------------------------------------------------------
// Cadence rules
// ---------------------------------------------------------------------------

/** ARR-derived tier — bigger relationships get tighter cadences. */
export type CadenceTier = 'enterprise' | 'mid' | 'standard';

/** A per-account cadence rule. Days between expected touches. */
export interface CadenceRule {
  /** A meaningful touch (call/email/meeting/note) at least this often. */
  checkInEveryDays: number;
  /** A deeper business review (QBR) at least this often. */
  qbrEveryDays: number;
}

/**
 * Sane defaults by tier. ARR buckets align with `score.ts`'s valuePoints
 * ($100k+ is the top bucket) so "enterprise" means the same thing everywhere.
 */
export const DEFAULT_CADENCE: Record<CadenceTier, CadenceRule> = {
  enterprise: { checkInEveryDays: 14, qbrEveryDays: 90 },
  mid: { checkInEveryDays: 21, qbrEveryDays: 120 },
  standard: { checkInEveryDays: 30, qbrEveryDays: 180 },
};

/** Map an account's ARR to its cadence tier. Unset ARR = standard. */
export function cadenceTierFor(arr: number | undefined): CadenceTier {
  if (arr !== undefined && arr >= 100_000) return 'enterprise';
  if (arr !== undefined && arr >= 25_000) return 'mid';
  return 'standard';
}

/** Optional per-account rule overrides, keyed by account id. */
export type CadenceOverrides = Record<string, Partial<CadenceRule>>;

// ---------------------------------------------------------------------------
// Due-touch computation
// ---------------------------------------------------------------------------

export interface DueTouch {
  account: Account;
  tier: CadenceTier;
  /** The resolved rule (defaults + overrides + paused relaxation). */
  rule: CadenceRule;
  /** ARR at stake (max across linked customers) — undefined when unset. */
  arr?: number;
  /** ISO of the freshest meaningful touch; undefined = never touched. */
  lastTouchAt?: string;
  /** Whole days since the touch anchor (createdAt when never touched). */
  daysSinceTouch: number;
  /** Whole days past the check-in cadence (always ≥ 1 in results). */
  overdueDays: number;
  neverTouched: boolean;
  /** Escalates to 'qbr' when the silence exceeds the QBR cadence too. */
  suggestedAction: 'check-in' | 'qbr';
  /** Human explanation, e.g. "Quiet 40d — 10d past the 30d standard cadence". */
  reason: string;
}

/** People linked to an account: `company` matches the account name. */
function linkedPeople(account: Account, people: Person[]): Person[] {
  const name = account.name.trim().toLowerCase();
  return people.filter((p) => (p.company ?? '').trim().toLowerCase() === name);
}

/** Whole days since `iso`, or undefined when unparsable/missing. */
function daysSince(iso: string | undefined, nowMs: number): number | undefined {
  if (!iso) return undefined;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return undefined;
  return Math.max(0, Math.floor((nowMs - t) / DAY_MS));
}

/** Latest of two optional ISO timestamps. */
function laterIso(a: string | undefined, b: string | undefined): string | undefined {
  if (!a) return b;
  if (!b) return a;
  return Date.parse(a) >= Date.parse(b) ? a : b;
}

/**
 * The freshest meaningful touch we have for an account: the newest entry on
 * its email trail, or the newest touch on any linked person's timeline.
 * Returns undefined when there is genuinely no signal — never guesses.
 */
export function accountLastTouchAt(account: Account, people: Person[]): string | undefined {
  let latest: string | undefined;
  for (const e of account.emails ?? []) latest = laterIso(latest, e.at);
  for (const p of linkedPeople(account, people)) latest = laterIso(latest, lastTouchAt(p));
  return latest;
}

/**
 * Compute which accounts are past their cadence, most-overdue first.
 *
 * Rules:
 *   - Tier from linked-customer ARR (max), status from linked customers.
 *   - All-churned accounts are excluded (no cadence for a dead relationship).
 *   - Paused-only accounts get a relaxed (doubled) check-in cadence.
 *   - Never-touched accounts are anchored to their createdAt: they surface
 *     only once the cadence grace has elapsed since creation.
 *   - Silence beyond the QBR cadence escalates the suggestion to a QBR.
 */
export function dueTouches(
  accounts: Account[],
  people: Person[],
  nowMs: number,
  overrides: CadenceOverrides = {},
): DueTouch[] {
  const out: DueTouch[] = [];

  for (const account of accounts) {
    const linked = linkedPeople(account, people);
    const customers = linked.filter((p) => p.category === 'customer');

    // Lifecycle status: skip dead relationships, relax paused ones.
    const statuses = customers.map((p) => p.status ?? 'active');
    if (statuses.length > 0 && statuses.every((s) => s === 'churned')) continue;
    const paused = statuses.length > 0 && statuses.every((s) => s === 'paused' || s === 'churned');

    // ARR at stake — the max across linked customers; undefined when unset.
    const values = customers
      .map((p) => p.accountValue)
      .filter((v): v is number => typeof v === 'number');
    const arr = values.length ? Math.max(...values) : undefined;

    const tier = cadenceTierFor(arr);
    const base = DEFAULT_CADENCE[tier];
    const override = overrides[account.id] ?? {};
    const rule: CadenceRule = {
      checkInEveryDays: (override.checkInEveryDays ?? base.checkInEveryDays) * (paused ? 2 : 1),
      qbrEveryDays: (override.qbrEveryDays ?? base.qbrEveryDays) * (paused ? 2 : 1),
    };

    const touchIso = accountLastTouchAt(account, people);
    const neverTouched = touchIso === undefined;
    // Never-touched accounts anchor to createdAt so a brand-new account gets
    // its full cadence as a grace window before being called overdue.
    const anchorDays = daysSince(touchIso ?? account.createdAt, nowMs);
    if (anchorDays === undefined) continue; // unparsable dates: no verdict.

    const overdueDays = anchorDays - rule.checkInEveryDays;
    if (overdueDays <= 0) continue; // on track.

    const suggestedAction: DueTouch['suggestedAction'] =
      !neverTouched && anchorDays > rule.qbrEveryDays ? 'qbr' : 'check-in';

    const reason = neverTouched
      ? `Never touched — ${overdueDays}d past the ${rule.checkInEveryDays}d ${tier} cadence since creation`
      : `Quiet ${anchorDays}d — ${overdueDays}d past the ${rule.checkInEveryDays}d ${tier} cadence`;

    out.push({
      account,
      tier,
      rule,
      arr,
      lastTouchAt: touchIso,
      daysSinceTouch: anchorDays,
      overdueDays,
      neverTouched,
      suggestedAction,
      reason,
    });
  }

  return out.sort((a, b) => b.overdueDays - a.overdueDays);
}

// ---------------------------------------------------------------------------
// Governed proposal builder
// ---------------------------------------------------------------------------

/** Deterministic proposal title — stable per account so re-runs de-dupe. */
export function cadenceTitleFor(due: DueTouch): string {
  return due.suggestedAction === 'qbr'
    ? `Schedule QBR — ${due.account.name}`
    : `Check in — ${due.account.name}`;
}

/**
 * Build the concrete `create_task` proposal for an overdue account. Tied to
 * the account (so it shows on its page), carrying the ARR at stake in
 * `value` — omitted when unset, never invented. Badly overdue (≥ 14d past
 * cadence) escalates to urgent.
 */
export function cadenceActionFor(due: DueTouch): CreateTaskAction {
  return {
    kind: 'create_task',
    title: cadenceTitleFor(due),
    priority: due.overdueDays >= 14 ? 'urgent' : 'high',
    dueDate: null,
    accountId: due.account.id,
    accountName: due.account.name,
    value: due.arr,
  };
}

/** Compact human label for a row, e.g. "10d overdue · last touch 40d ago". */
export function overdueLabel(due: DueTouch): string {
  const tail = due.neverTouched ? 'never touched' : `last touch ${due.daysSinceTouch}d ago`;
  return `${due.overdueDays}d overdue · ${tail}`;
}
